import { queryOptions } from "@tanstack/react-query";

import { getOperatorsHttpOrigin } from "../../operators";
import {
  readOperatorsAuthErrorMessage,
  throwOperatorsAuthExpired,
} from "../../operatorsAuthSession";
import { OperatorsForbiddenError, operatorsQueryKeys } from "../../lib/operatorsReactQuery";
import { type DataDictionaryResponse, isDataDictionaryResponse } from "./dataDictionaryTypes";

/** Domain-local query helpers for Data Dictionary. */
export const dataDictionaryQueryKeys = {
  all: [...operatorsQueryKeys.all, "dashboards", "data-dictionary"] as const,
  schema: () => [...dataDictionaryQueryKeys.all, "schema"] as const,
};

function throwIfUnauthorized(response: Response): void {
  if (response.status === 401) {
    throwOperatorsAuthExpired();
  }
}

async function throwIfForbidden(response: Response, fallbackMessage: string): Promise<void> {
  if (response.status !== 403) {
    return;
  }
  const message = await readOperatorsAuthErrorMessage(response).catch(() => fallbackMessage);
  throw new OperatorsForbiddenError(message || fallbackMessage);
}

export async function fetchDataDictionarySchema(): Promise<DataDictionaryResponse> {
  const response = await fetch(`${getOperatorsHttpOrigin()}/api/v1/dashboards/data-dictionary`, {
    credentials: "include",
  });
  throwIfUnauthorized(response);
  await throwIfForbidden(response, "You do not have access to Dashboards.");
  if (!response.ok) {
    throw new Error(
      await readOperatorsAuthErrorMessage(response).catch(
        () => "Schema explorer could not be loaded",
      ),
    );
  }
  const body: unknown = await response.json();
  if (!isDataDictionaryResponse(body)) {
    throw new Error("Data Dictionary response was malformed");
  }
  return body;
}

export function dataDictionarySchemaQueryOptions() {
  return queryOptions({
    queryKey: dataDictionaryQueryKeys.schema(),
    queryFn: fetchDataDictionarySchema,
  });
}

/** Absolute iframe URL for the auth-gated ERD bundle (includes embed token). */
export function buildDataDictionaryErdFrameUrl(schema: DataDictionaryResponse): string {
  const origin = getOperatorsHttpOrigin().replace(/\/+$/, "");
  const erdPath = schema.erdUrl.startsWith("/") ? schema.erdUrl : `/${schema.erdUrl}`;
  const url = new URL(`${origin}${erdPath}`);
  url.searchParams.set("productionSha", schema.sha);
  url.searchParams.set("embedToken", schema.embedToken);
  return url.toString();
}

/**
 * `allow-scripts allow-same-origin` is only a safe sandbox configuration when
 * the embedded ERD is served from a different origin than the parent UI.
 */
export function isDataDictionaryErdFrameCrossOrigin(frameUrl: string): boolean {
  const parentOrigin = typeof window === "undefined" ? null : window.location.origin;
  return parentOrigin !== null && new URL(frameUrl).origin !== parentOrigin;
}
